import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { DEFAULT_WEBINAR_IMPORT_KEY, getDadRelationshipStage } from './memberConstants';

const EMPTY_DASHBOARD = {
  active_campaigns: [],
  recent_imports: [],
};

const INITIAL_IMPORT_DEFAULTS = {
  relationship_stage: 'webinar_registered',
  campaign_type: 'dad_partners_founding_forty',
  source: '',
  warmth: '',
};

const INITIAL_CAMPAIGN_FORM = {
  sequence_id: '',
  step_key: '',
  delivery_mode: 'send_now',
  auto_chain_sequence: true,
  batch_size: 20,
  cadence_type: 'daily',
  cadence_interval: 1,
  start_date: '',
  start_time: '07:00',
  chain_delays: {
    email_2: 1,
    email_3: 4,
  },
};

export const useDadOutreachWorkflow = ({
  adminFetch,
  fetchMembers,
  handleImportDads,
  importingDads,
  joinFirstMode,
  dadMembers,
  filteredMembers,
}) => {
  const [showImportModal, setShowImportModal] = useState(false);
  const [showWebinarImportModal, setShowWebinarImportModal] = useState(false);
  const [showSequenceModal, setShowSequenceModal] = useState(false);
  const [importCsv, setImportCsv] = useState('');
  const [webinarImportCsv, setWebinarImportCsv] = useState('');
  const [webinarImportKey, setWebinarImportKey] = useState(DEFAULT_WEBINAR_IMPORT_KEY);
  const [selectedDadIds, setSelectedDadIds] = useState([]);
  const [sequences, setSequences] = useState([]);
  const [loadingSequences, setLoadingSequences] = useState(false);
  const [dashboardLoading, setDashboardLoading] = useState(false);
  const [dadCampaignDashboard, setDadCampaignDashboard] = useState(EMPTY_DASHBOARD);
  const [previewingCampaign, setPreviewingCampaign] = useState(false);
  const [campaignSubmitting, setCampaignSubmitting] = useState(false);
  const [webinarImporting, setWebinarImporting] = useState(false);
  const [campaignPreview, setCampaignPreview] = useState(null);
  const [importDefaults, setImportDefaults] = useState(INITIAL_IMPORT_DEFAULTS);
  const [campaignForm, setCampaignForm] = useState(INITIAL_CAMPAIGN_FORM);

  const allDadIds = useMemo(
    () => dadMembers.map((member) => member.user_id).filter(Boolean),
    [dadMembers],
  );

  const visibleProspectIds = useMemo(
    () => filteredMembers
      .filter((member) => getDadRelationshipStage(member) === 'prospect')
      .map((member) => member.user_id)
      .filter(Boolean),
    [filteredMembers],
  );

  const visibleWebinarInvitedIds = useMemo(
    () => filteredMembers
      .filter((member) => getDadRelationshipStage(member) === 'webinar_invited')
      .map((member) => member.user_id)
      .filter(Boolean),
    [filteredMembers],
  );

  useEffect(() => {
    const validIds = new Set(allDadIds);
    setSelectedDadIds((prev) => prev.filter((userId) => validIds.has(userId)));
  }, [allDadIds]);

  const selectedSequence =
    sequences.find((sequence) => sequence.sequence_id === campaignForm.sequence_id) || sequences[0] || null;
  const selectedSequenceSteps = selectedSequence?.steps || [];
  const selectedStep =
    selectedSequenceSteps.find((step) => step.step_key === campaignForm.step_key) || selectedSequenceSteps[0] || null;
  const selectedStepOrder = selectedStep?.order || 0;
  const remainingSequenceSteps = selectedSequenceSteps.filter((step) => (step.order || 0) > selectedStepOrder);

  useEffect(() => {
    if (!selectedSequence) return;

    const hasStep = selectedSequence.steps?.some((step) => step.step_key === campaignForm.step_key);
    if (!hasStep) {
      setCampaignForm((prev) => ({
        ...prev,
        sequence_id: selectedSequence.sequence_id,
        step_key: selectedSequence.steps?.[0]?.step_key || '',
      }));
      return;
    }

    if (campaignForm.sequence_id !== selectedSequence.sequence_id) {
      setCampaignForm((prev) => ({
        ...prev,
        sequence_id: selectedSequence.sequence_id,
      }));
    }
  }, [selectedSequence, campaignForm.sequence_id, campaignForm.step_key]);

  const fetchSequences = async () => {
    setLoadingSequences(true);
    try {
      const response = await adminFetch('/admin/dads/email-sequences');
      if (!response.ok) {
        throw new Error('Failed to load dad webinar email sequences');
      }

      const data = await response.json();
      const sequenceList = data.sequences || [];
      setSequences(sequenceList);
      setCampaignForm((prev) => {
        const nextSequence =
          sequenceList.find((sequence) => sequence.sequence_id === prev.sequence_id) ||
          sequenceList[0];
        const nextStep =
          nextSequence?.steps?.find((step) => step.step_key === prev.step_key) ||
          nextSequence?.steps?.[0];

        return {
          ...prev,
          sequence_id: nextSequence?.sequence_id || '',
          step_key: nextStep?.step_key || '',
        };
      });
    } catch (error) {
      console.error('Failed to load dad webinar email sequences:', error);
      setSequences([]);
    } finally {
      setLoadingSequences(false);
    }
  };

  const fetchDadCampaignDashboard = useCallback(async () => {
    setDashboardLoading(true);
    try {
      const response = await adminFetch('/admin/dads/dashboard');
      if (!response.ok) {
        throw new Error('Failed to load dad campaign dashboard');
      }

      const data = await response.json();
      setDadCampaignDashboard({
        active_campaigns: Array.isArray(data.active_campaigns) ? data.active_campaigns : [],
        recent_imports: Array.isArray(data.recent_imports) ? data.recent_imports : [],
      });
    } catch (error) {
      console.error('Failed to load dad campaign dashboard:', error);
      setDadCampaignDashboard(EMPTY_DASHBOARD);
    } finally {
      setDashboardLoading(false);
    }
  }, [adminFetch]);

  useEffect(() => {
    fetchDadCampaignDashboard();
  }, [fetchDadCampaignDashboard]);

  useEffect(() => {
    if (!showSequenceModal || selectedDadIds.length === 0 || !campaignForm.sequence_id || !campaignForm.step_key) {
      return;
    }

    const previewCampaign = async () => {
      setPreviewingCampaign(true);
      try {
        const response = await adminFetch('/admin/dads/campaigns/preview', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            user_ids: selectedDadIds,
            sequence_id: campaignForm.sequence_id,
            step_key: campaignForm.step_key,
          }),
        });

        if (!response.ok) {
          const error = await response.json().catch(() => ({}));
          throw new Error(error.detail || 'Failed to preview dad webinar campaign');
        }

        const data = await response.json();
        setCampaignPreview(data);
      } catch (error) {
        setCampaignPreview(null);
        toast.error(error?.message || 'Failed to preview dad webinar campaign');
      } finally {
        setPreviewingCampaign(false);
      }
    };

    previewCampaign();
  }, [adminFetch, showSequenceModal, selectedDadIds, campaignForm.sequence_id, campaignForm.step_key]);

  const handleImportFile = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const text = await file.text();
    setImportCsv(text);
    event.target.value = '';
  };

  const handleWebinarImportFile = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const text = await file.text();
    setWebinarImportCsv(text);
    event.target.value = '';
  };

  const submitDadImport = async () => {
    const success = await handleImportDads({
      raw_csv: importCsv,
      ...importDefaults,
    });
    if (success) {
      setShowImportModal(false);
      setImportCsv('');
    }
  };

  const submitWebinarImport = async () => {
    if (!webinarImportCsv.trim()) {
      toast.error('Paste or load the webinar results CSV first');
      return;
    }

    if (!webinarImportKey.trim()) {
      toast.error('Enter a webinar import key');
      return;
    }

    setWebinarImporting(true);
    try {
      const response = await adminFetch('/admin/dads/webinar-results/import', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          raw_csv: webinarImportCsv,
          webinar_import_key: webinarImportKey,
        }),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.detail || 'Failed to import webinar results');
      }

      const data = await response.json();
      setShowWebinarImportModal(false);
      setWebinarImportCsv('');
      toast.success(
        `Imported ${data.imported_count || 0} webinar results (${data.attended_count || 0} attended, ${data.no_show_count || 0} missed)${data.followup_campaigns_created ? ' and created follow-up campaigns' : ''}`,
      );
      await fetchMembers();
      await fetchDadCampaignDashboard();
    } catch (error) {
      toast.error(error?.message || 'Failed to import webinar results');
    } finally {
      setWebinarImporting(false);
    }
  };

  const toggleDadSelection = (userId) => {
    setSelectedDadIds((prev) => (
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId]
    ));
  };

  const clearSelection = () => {
    setSelectedDadIds([]);
  };

  const toggleGroupSelection = (groupIds) => {
    if (groupIds.length === 0) return;

    setSelectedDadIds((prev) => {
      const everySelected = groupIds.every((userId) => prev.includes(userId));
      if (everySelected) {
        return prev.filter((userId) => !groupIds.includes(userId));
      }

      const next = new Set(prev);
      groupIds.forEach((userId) => next.add(userId));
      return Array.from(next);
    });
  };

  const openSequenceModal = async () => {
    if (selectedDadIds.length === 0) {
      toast.error('Select at least one dad first');
      return;
    }

    if (sequences.length === 0) {
      await fetchSequences();
    }

    setCampaignPreview(null);
    setShowSequenceModal(true);
  };

  const closeSequenceModal = () => {
    setShowSequenceModal(false);
    setCampaignPreview(null);
  };

  const handleCampaignFormChange = (field, value) => {
    setCampaignForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleChainDelayChange = (stepKey, value) => {
    setCampaignForm((prev) => ({
      ...prev,
      chain_delays: {
        ...prev.chain_delays,
        [stepKey]: value,
      },
    }));
  };

  const handleCreateCampaign = async () => {
    if (!campaignForm.sequence_id || !campaignForm.step_key) {
      toast.error('Choose a sequence and email step before sending');
      return;
    }

    const payload = {
      user_ids: selectedDadIds,
      sequence_id: campaignForm.sequence_id,
      step_key: campaignForm.step_key,
      delivery_mode: campaignForm.delivery_mode,
    };

    if (campaignForm.auto_chain_sequence && remainingSequenceSteps.length > 0) {
      payload.auto_chain_sequence = true;
      payload.chain_delays = remainingSequenceSteps.reduce((acc, step) => {
        acc[step.step_key] = Number(campaignForm.chain_delays?.[step.step_key] || 0);
        return acc;
      }, {});
    }

    if (campaignForm.delivery_mode === 'staggered') {
      const batchSize = Number(campaignForm.batch_size);
      const cadenceInterval = Number(campaignForm.cadence_interval);

      if (!campaignForm.start_date || !campaignForm.start_time) {
        toast.error('Choose a start date and time for staggered delivery');
        return;
      }

      const startAt = new Date(`${campaignForm.start_date}T${campaignForm.start_time}`);
      if (Number.isNaN(startAt.getTime())) {
        toast.error('Enter a valid staggered delivery start time');
        return;
      }

      if (!Number.isFinite(batchSize) || batchSize < 1) {
        toast.error('Batch size must be at least 1');
        return;
      }

      if (!Number.isFinite(cadenceInterval) || cadenceInterval < 1) {
        toast.error('Cadence interval must be at least 1');
        return;
      }

      payload.batch_size = batchSize;
      payload.cadence_type = campaignForm.cadence_type;
      payload.cadence_interval = cadenceInterval;
      payload.start_at = startAt.toISOString();
    }

    setCampaignSubmitting(true);
    try {
      const response = await adminFetch('/admin/dads/campaigns', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.detail || (joinFirstMode ? 'Failed to create dad outreach campaign' : 'Failed to create dad webinar campaign'));
      }

      const data = await response.json();
      setCampaignPreview(data.preview || null);
      clearSelection();
      setShowSequenceModal(false);
      await fetchMembers();
      await fetchDadCampaignDashboard();
      toast.success(
        payload.delivery_mode === 'staggered'
          ? `Scheduled ${data.preview?.recipient_count || 0} ${joinFirstMode ? 'outreach email' : 'webinar invite'}${(data.preview?.recipient_count || 0) === 1 ? '' : 's'}${data.chained_campaigns?.length ? ` and ${data.chained_campaigns.length} follow-up step${data.chained_campaigns.length === 1 ? '' : 's'}` : ''}`
          : `Sent ${data.preview?.recipient_count || 0} ${joinFirstMode ? 'outreach email' : 'webinar invite'}${(data.preview?.recipient_count || 0) === 1 ? '' : 's'}${data.chained_campaigns?.length ? ` and scheduled ${data.chained_campaigns.length} follow-up step${data.chained_campaigns.length === 1 ? '' : 's'}` : ''}`,
      );
    } catch (error) {
      toast.error(error?.message || (joinFirstMode ? 'Failed to create dad outreach campaign' : 'Failed to create dad webinar campaign'));
    } finally {
      setCampaignSubmitting(false);
    }
  };

  return {
    importingDads,
    showImportModal,
    showWebinarImportModal,
    showSequenceModal,
    importCsv,
    webinarImportCsv,
    webinarImportKey,
    selectedDadIds,
    sequences,
    loadingSequences,
    dashboardLoading,
    dadCampaignDashboard,
    previewingCampaign,
    campaignSubmitting,
    webinarImporting,
    campaignPreview,
    importDefaults,
    campaignForm,
    visibleProspectIds,
    visibleWebinarInvitedIds,
    selectedSequenceSteps,
    selectedStep,
    remainingSequenceSteps,
    setImportDefaults,
    setImportCsv,
    setWebinarImportCsv,
    setWebinarImportKey,
    openDadImportModal: () => setShowImportModal(true),
    closeDadImportModal: () => setShowImportModal(false),
    openWebinarImportModal: () => setShowWebinarImportModal(true),
    closeWebinarImportModal: () => setShowWebinarImportModal(false),
    toggleDadSelection,
    clearSelection,
    toggleGroupSelection,
    openSequenceModal,
    closeSequenceModal,
    handleCampaignFormChange,
    handleChainDelayChange,
    handleCreateCampaign,
    handleImportFile,
    handleWebinarImportFile,
    submitDadImport,
    submitWebinarImport,
  };
};
